import {
  Workout,
  workoutDurationSeconds,
  workoutReps,
  workoutDistanceMeters,
  WeightUnit,
  DistanceUnitShort,
} from 'hevy-shared';
import { userWeight, userDistance } from 'utils/units';
import {
  StatProps,
  WorkoutContentExerciseCell,
  WorkoutContentMediaCell,
  WorkoutContentProps,
} from './types';
import { sum } from 'lodash';
import { t } from 'translations/t';
import { formatNumberWithUnit } from 'translations/formatters';
import { secondsToWordFormatMinutesLocalized } from 'translations/units';
import { stores } from 'state/stores';

interface Preferences {
  weightUnit: WeightUnit;
  distanceUnitShort: DistanceUnitShort;
  defaultRestTimerSeconds: number;
}

export const calculateWorkoutStats = (workout: Workout, preferences: Preferences): StatProps[] => {
  const volumeKg = workout.estimated_volume_kg;
  const durationSeconds = workoutDurationSeconds(workout);
  const reps = workoutReps(workout);
  const distanceMeters = workoutDistanceMeters(workout);

  const stats: StatProps[] = [
    {
      title: t('web.workoutCell.duration'),
      value: secondsToWordFormatMinutesLocalized(durationSeconds),
      isPr: false,
    },
  ];

  const userVolume = userWeight(volumeKg, preferences.weightUnit);
  const distance = userDistance(distanceMeters, preferences.distanceUnitShort);

  const personalRecordCount: number = workout.exercises.reduce((accu, e) => {
    return accu + sum(e.sets.map(s => s.prs?.length || 0));
  }, 0);

  if (volumeKg > reps) {
    stats.push({
      title: t('web.workoutCell.volume'),
      value: formatNumberWithUnit(userVolume, preferences.weightUnit, { maximumFractionDigits: 0 }),
      isPr: false,
    });
  } else if (reps > distanceMeters) {
    stats.push({ title: t('web.workoutCell.reps'), value: `${reps}`, isPr: false });
  } else if (distance > 0) {
    stats.push({
      title: t('web.workoutCell.distance'),
      value: formatNumberWithUnit(distance, preferences.distanceUnitShort, {
        maximumFractionDigits: 2,
      }),
      isPr: false,
    });
  }

  if (personalRecordCount) {
    stats.push({
      title: t('web.workoutCell.records'),
      value: `${personalRecordCount}`,
      isPr: true,
    });
  }

  return stats;
};

export const calculateWorkoutContent = (workout: Workout): WorkoutContentProps => {
  const exercises = workout.exercises.map((e, index) => {
    const setPluralized =
      e.sets.length === 1 ? t('web.workoutCell.set.singular') : t('web.workoutCell.sets.plural');
    const exerciseTitle =
      stores.exerciseTemplates.getExerciseTitle(e.exercise_template_id) || e.title;
    const title = t('web.workoutCell.exerciseSets', {
      numSets: e.sets.length,
      setsPluralized: setPluralized,
      exerciseTitle,
    });

    return {
      key: `${index}`,
      id: e.exercise_template_id,
      superset: e.superset_id ?? undefined,
      title,
      image: e.thumbnail_url ?? undefined,
    };
  });

  const truncatedExerciseCount = exercises.length - 3;
  const exercisePluralized =
    truncatedExerciseCount === 1
      ? t('web.workoutCell.exercise.singular')
      : t('web.workoutCell.exercises.plural');
  const truncatedExercisesTitle =
    truncatedExerciseCount > 0
      ? t('web.workoutCell.seeMoreTruncatedExercises', {
          truncatedExerciseCount,
          exercisePluralized,
        })
      : undefined;

  const mediaCells: WorkoutContentMediaCell[] = workout.media
    .filter(m => m.type === 'image')
    .map(m => ({
      cellType: 'media',
      url: m.url,
      mediaType: m.type,
    }));
  const exerciseCell: WorkoutContentExerciseCell = {
    cellType: 'exercises',
    exercises: exercises.slice(0, 3),
    truncatedExercisesTitle,
  };

  return {
    cells: [...mediaCells, exerciseCell],
  };
};
